import z from 'zod';

import { reportError } from '../sentry.ts';
import { pushToDataLayer } from '../tracking.ts';

import courseLead from './course-lead.ts';
import debug from './debug.ts';
import googleSheets from './google-sheets.ts';
import pardot from './pardot.ts';
import { type FormSubmitHandler } from './types.ts';

// Erased registry, used to dispatch by name. `handle()` stays callable because
// `FormSubmitHandler` uses bivariant method syntax.
const handlers: Record<string, FormSubmitHandler | undefined> = {
    courseLead,
    debug,
    googleSheets,
    pardot,
};

// A handler argument is a name plus an opaque options bag. The options are
// validated separately, by the chosen handler's own schema, once the handler
// has been looked up.
const HandlerArgSchema = z.object({
    name: z.string(),
    options: z.record(z.string(), z.unknown()),
});

/** Best-effort schema for showing the configured message when options are invalid. */
const ErrorMessageSchema = z.object({
    options: z.object({ errorMessage: z.string() }),
});

type FormSubmitHandlerArgs = {
    handler: unknown;
    $form: HTMLFormElement;
    $message: HTMLElement;
};

export default function formSubmitHandler({
    handler: rawHandler,
    $form,
    $message,
}: FormSubmitHandlerArgs) {
    const $submits = Array.from($form.elements)
        .filter(
            (el) =>
                el instanceof HTMLButtonElement ||
                el instanceof HTMLInputElement,
        )
        .filter((el) => el.type === 'submit');

    let isSubmitting = false;
    let isDisabled = false;
    let message: string | null = null;

    const update = () => {
        $submits.forEach((el) => {
            el.disabled = isSubmitting || isDisabled;
        });

        $message.classList.toggle('hidden', !message);
        $message.innerHTML = message ?? '';
    };

    // Report a fatal setup error, show the configured message if we can find
    // one, and disable the form so it cannot be submitted.
    const reportAndDisable = (error: unknown, handlerName?: string) => {
        // Prefer the parsed handler name; fall back to the whole raw value only
        // before the envelope has been validated, when no name is known yet.
        reportError(error, { handler: handlerName ?? rawHandler });

        const fallback = ErrorMessageSchema.safeParse(rawHandler);
        message = fallback.success
            ? fallback.data.options.errorMessage
            : 'Failed to load the form options.';
        isDisabled = true;

        update();
    };

    const handlerResult = HandlerArgSchema.safeParse(rawHandler);

    if (!handlerResult.success) {
        reportAndDisable(handlerResult.error);

        return;
    }

    const { name: handlerName, options: rawOptions } = handlerResult.data;

    const handler = handlers[handlerName];

    if (!handler) {
        reportAndDisable(
            new Error(`Invalid handler '${handlerName}'.`),
            handlerName,
        );

        return;
    }

    const optionsResult = handler.optionsSchema.safeParse(rawOptions);

    if (!optionsResult.success) {
        reportAndDisable(optionsResult.error, handlerName);

        return;
    }

    const options = optionsResult.data;

    const handleSubmit = (event: SubmitEvent) => {
        event.preventDefault();

        if (isSubmitting) {
            return;
        }

        // This does not support `name="foo[]"`
        const dataResult = handler.dataSchema.safeParse(
            Object.fromEntries(new FormData($form)),
        );

        if (!dataResult.success) {
            reportError(dataResult.error, { handler: handlerName });

            message = options.errorMessage;

            update();

            return;
        }

        handler
            .handle(dataResult.data, options)
            .then((responseMessage) => {
                message = responseMessage;

                $form.reset();

                if (options.submitEvent) {
                    pushToDataLayer(options.submitEvent);
                }
            })
            .catch((error: Error) => {
                message = error.message;
            })
            .finally(() => {
                isSubmitting = false;

                update();
            });

        isSubmitting = true;

        update();
    };

    $form.addEventListener('submit', handleSubmit);
}
